import React, { useState } from 'react';
import { ArrowRight, ExternalLink } from 'lucide-react';

interface OrderFormProps {
  roastType: string;
  purchaseType: 'monthly' | 'single';
  selectedOption: {
    id: string;
    months: number;
    monthly_price: number | null;
    upfront_price: number | null;
    single_price: number | null;
    bags: number;
    size_oz: number;
    label: string;
    savings_text: string;
    popular: boolean;
    active: boolean;
    is_subscription: boolean;
  } | any; // Allow Marblehead pricing tier as well
  email: string;
  quizSkipped?: boolean;
  isMarblehead?: boolean;
  selectedTier?: any;
  onSuccess: (name: string, isMarblehead: boolean, selectedTier: any) => void;
  onBack: () => void;
}

// Stripe Payment Links Mapping
const STRIPE_PAYMENT_LINKS = {
  // Monthly subscriptions
  'monthly-marblehead': 'https://buy.stripe.com/14AfZifq7epp5VP5VE3VC06',
  'monthly-marblehead-2bags': 'https://buy.stripe.com/9B6bJ2cdVgxx1Fzesa3VC01',
  'monthly-marblehead-local-noon': 'https://buy.stripe.com/aFadRagub5ST2JD2Js3VC0m',
  'monthly-non-marblehead': 'https://buy.stripe.com/14AaEY5Pxepp3NH4RA3VC07',

  // Single purchases - Marblehead
  'festival-blend-marblehead': 'https://buy.stripe.com/5kQeVe6TB4OP2JD1Fo3VC0k',
  'regiment-roast-marblehead': 'https://buy.stripe.com/9B6dRa6TB1CDbg92Js3VC0i',
  'wake-with-drake-marblehead': 'https://buy.stripe.com/8x2bJ2dhZfttckdck23VC0g',
  'barnegat-marblehead': 'https://buy.stripe.com/3cI14o1zhdll9814RA3VC05',
  'soleless-marblehead': 'https://buy.stripe.com/eVq14ob9R6WXbg90Bk3VC04',
  'nunnepog-marblehead': 'https://buy.stripe.com/aFa3cw6TB6WX4RL83M3VC08',

  // Single purchases - Non-Marblehead
  'festival-blend-non-marblehead': 'https://buy.stripe.com/6oUeVefq7eppesl4RA3VC0l',
  'regiment-roast-non-marblehead': 'https://buy.stripe.com/aFaaEY6TBdllfwpabU3VC0j',
  'wake-with-drake-non-marblehead': 'https://buy.stripe.com/fZu14o7XF9552JD3Nw3VC0h',
  'barnegat-non-marblehead': 'https://buy.stripe.com/6oUcN691Jdll6ZT97Q3VC03',
  'soleless-non-marblehead': 'https://buy.stripe.com/8x24gA91Jftt5VPck23VC02',
  'nunnepog-non-marblehead': 'https://buy.stripe.com/eVqfZigub3KL6ZT0Bk3VC09'
};

export default function OrderForm({ 
  roastType, 
  purchaseType, 
  selectedOption, 
  email,
  quizSkipped = false,
  isMarblehead = false,
  selectedTier,
  onSuccess, 
  onBack 
}: OrderFormProps) {

  const getPaymentLink = () => {
    // Determine the correct payment link based on selection
    if (purchaseType === 'monthly') {
      if (isMarblehead) {
        if (selectedTier?.pickupMethod === 'local-noon') {
          return STRIPE_PAYMENT_LINKS['monthly-marblehead-local-noon'];
        }
        const bags = selectedTier?.selectedBags || 1;
        if (bags === 2) {
          return STRIPE_PAYMENT_LINKS['monthly-marblehead-2bags'];
        } else {
          return STRIPE_PAYMENT_LINKS['monthly-marblehead'];
        }
      } else {
        return STRIPE_PAYMENT_LINKS['monthly-non-marblehead'];
      }
    } else {
      // Single purchase
      if (isMarblehead) {
        if (roastType === 'regiment-roast') {
          return STRIPE_PAYMENT_LINKS['regiment-roast-marblehead'];
        } else if (roastType === 'wake-with-drake') {
          return STRIPE_PAYMENT_LINKS['wake-with-drake-marblehead'];
        } else if (roastType === 'barnegat-breakfast') {
          return STRIPE_PAYMENT_LINKS['barnegat-marblehead'];
        } else if (roastType === 'soleless-shakedown') {
          return STRIPE_PAYMENT_LINKS['soleless-marblehead'];
        } else if (roastType === 'nunnepog-fog') {
          return STRIPE_PAYMENT_LINKS['nunnepog-marblehead'];
        }
      } else {
        if (roastType === 'regiment-roast') {
          return STRIPE_PAYMENT_LINKS['regiment-roast-non-marblehead'];
        } else if (roastType === 'wake-with-drake') {
          return STRIPE_PAYMENT_LINKS['wake-with-drake-non-marblehead'];
        } else if (roastType === 'barnegat-breakfast') {
          return STRIPE_PAYMENT_LINKS['barnegat-non-marblehead'];
        } else if (roastType === 'soleless-shakedown') {
          return STRIPE_PAYMENT_LINKS['soleless-non-marblehead'];
        } else if (roastType === 'nunnepog-fog') {
          return STRIPE_PAYMENT_LINKS['nunnepog-non-marblehead'];
        }
      }
    }

    // Fallback - shouldn't happen but just in case
    return STRIPE_PAYMENT_LINKS['barnegat-non-marblehead'];
  };

  const handleProceedToPayment = () => {
    // Check if Wake with Drake is sold out
    if (roastType === 'wake-with-drake') {
      return; // Don't proceed if sold out
    }

    const paymentLink = getPaymentLink();

    // Redirect to Stripe payment link
    window.open(paymentLink, '_blank');

    // Show success message after a short delay (assuming they'll complete payment)
    setTimeout(() => {
      onSuccess('Customer', isMarblehead, selectedTier);
    }, 2000);
  };

  const roastEmojis = {
    light: '🕊️',
    medium: '🌿',
    dark: '🌋',
    espresso: '🚀',
    'regiment-roast': '⚓',
    'wake-with-drake': '🏈',
    'barnegat-breakfast': '🌅',
    'soleless-shakedown': '⚡',
    'nunnepog-fog': '🌫️'
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <div className="text-6xl mb-4">
          {roastEmojis[roastType as keyof typeof roastEmojis]}
        </div>
        <h2 className="text-3xl font-bold text-white">
          Almost There! Let's Get This Coffee to You
        </h2>
        <p className="text-lg text-white/80">
          Just a few details and you'll be redirected to secure Stripe checkout.
        </p>
      </div>

      {/* Order Summary */}
      <div className="bg-gradient-to-br from-[#fda36b]/10 to-[#fda36b]/5 p-6 rounded-lg border border-[#fda36b]/20">
        <h3 className="text-xl font-semibold text-[#fda36b] mb-4">Your Order</h3>
        {isMarblehead && selectedTier && (
          <div className="mb-4 p-3 bg-[#fda36b]/20 rounded-lg">
            <p className="text-white font-semibold text-sm">
              🏠 Marblehead Community Pricing - {selectedTier.tier_name}
            </p>
          </div>
        )}
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-white/90">Selection:</span>
            <span className="text-white font-semibold capitalize">
              {purchaseType === 'monthly'
                ? 'Monthly Roasts (Variety)'
                : roastType === 'regiment-roast' ? 'Regiment Roast' :
                  roastType === 'wake-with-drake' ? 'Wake with Drake' :
                  roastType === 'barnegat-breakfast' ? 'Barnegat Breakfast' :
                  roastType === 'soleless-shakedown' ? 'Soleless Shakedown' :
                  roastType === 'nunnepog-fog' ? 'Nunnepog Fog' :
                  roastType
              }
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-white/90">Delivery:</span>
            <span className="text-white font-semibold capitalize">
              {isMarblehead
                ? selectedTier?.pickupMethod === 'local-noon'
                  ? 'Pickup at Local Noon — 92 Washington St'
                  : 'Personal Delivery (Free)'
                : purchaseType === 'monthly' ? 'Shipping (Included in $27)' : 'Shipping (Included)'
              }
            </span>
          </div>
          <div className="flex justify-between items-center text-lg pt-2 border-t border-white/20">
            <span className="text-[#fda36b] font-semibold">
              {purchaseType === 'single' ? 'Price (1 bag):' : 
               selectedTier?.selectedBags > 1 ? `Price (${selectedTier.selectedBags} bags):` : 'Starting Price:'}
            </span>
            <span className="text-[#fda36b] font-bold text-xl">
              {isMarblehead ?
                `$${((selectedTier?.selectedBags || 1) * (selectedTier?.pickupMethod === 'local-noon' ? 16 : 19)).toFixed(2)}` :
                `$${((selectedTier?.selectedBags || 1) * 27).toFixed(2)}`
              }
              {purchaseType === 'monthly' ? '/month' : ''}
            </span>
          </div>
          {purchaseType === 'single' ? (
            <p className="text-white/70 text-sm text-center pt-2">
              Want more than one bag? You can easily add more during checkout!
            </p>
          ) : (
            <p className="text-white/70 text-sm text-center pt-2">
              You can choose multiple bags and add-ons during Stripe checkout
            </p>
          )}
        </div>
      </div>

      {/* Multiple Bags Info */}
      <div className="bg-gradient-to-br from-[#fda36b]/10 to-[#fda36b]/5 p-4 rounded-lg border border-[#fda36b]/20">
        <p className="text-white/90 text-sm text-center leading-relaxed">
          {purchaseType === 'single' ? (
            <>
              <span className="font-semibold text-[#fda36b]">Need more than one bag?</span> No problem! 
              During Stripe checkout, you can easily change the quantity to 2, 3, or however many bags you want. 
              Perfect for stocking up or sharing with friends!
            </>
          ) : (
            <>
              <span className="font-semibold text-[#fda36b]">Want more coffee per month?</span> You can easily 
              adjust to 2, 3, or more bags per month during Stripe checkout. 
              Perfect for households with multiple coffee drinkers or if you just really love great coffee!
            </>
          )}
        </p>
      </div>

      {/* Action Buttons */}
      <div className="space-y-4">
        <div className="space-y-4">
          {/* Proceed to Payment Button */}
          <button
            onClick={handleProceedToPayment}
            disabled={roastType === 'wake-with-drake'}
            className={`w-full px-6 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center gap-2 ${
              roastType === 'wake-with-drake'
                ? 'bg-gray-600 text-white/50 cursor-not-allowed'
                : 'bg-[#635bff] text-white hover:bg-[#5a52e8]'
            }`}
          >
            <ExternalLink className="w-5 h-5" />
            {roastType === 'wake-with-drake' ? 'Sold Out' : 'Proceed to Secure Stripe Checkout'}
          </button>
          
          <div className="bg-[#fda36b]/10 p-4 rounded-lg border border-[#fda36b]/20">
            <p className="text-white/90 text-sm text-center leading-relaxed">
              <span className="font-semibold text-[#fda36b]">Secure Payment:</span> You'll be redirected to Stripe's secure checkout page where you can enter your details and pay with any major credit card, Apple Pay, or Google Pay. Your information is protected by bank-level security.
            </p>
          </div>
          
          <button
            onClick={onBack}
            className="w-full bg-white/10 text-white px-6 py-4 rounded-lg font-semibold hover:bg-white/20 transition-all duration-300"
          >
            Back to Selection
          </button>
        </div>

      </div>

      {/* What Happens Next */}
      <div className="bg-gradient-to-br from-white/10 to-white/5 p-6 rounded-lg border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">What Happens Next?</h3>
        <div className="space-y-3 text-sm text-white/80">
          <div className="flex items-start gap-3">
            <span className="text-[#fda36b] font-bold">1.</span>
            <span>Click "Proceed to Secure Stripe Checkout" to open Stripe's payment page</span>
          </div>
          <div className="flex items-start gap-3">
            <span className="text-[#fda36b] font-bold">2.</span>
            <span>
              Enter your contact info, payment details, and shipping address on Stripe's secure page
              <span className="text-[#fda36b]"> (you can adjust quantity here too!)</span>
            </span>
          </div>
          <div className="flex items-start gap-3">
            <span className="text-[#fda36b] font-bold">3.</span>
            <span>
              {isMarblehead && selectedTier?.pickupMethod === 'local-noon'
                ? "We'll drop your fresh coffee at Local Noon (92 Washington St) — just pop in and pick it up!"
                : isMarblehead
                ? "We'll personally deliver your fresh coffee (probably in the Japanese mini truck!)"
                : "Your coffee will be shipped fresh to your door within 2-3 business days"
              }
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}